// ---------------------------------------------------------------------------
// CLIENT-ONBOARDING-UI-0001 R1 — Typed client onboarding draft model
//
// This model represents the local-only draft state for the 8-step client
// onboarding wizard.  It is intentionally NOT coupled to DynamoDB records,
// Cognito user pools, or TTN entities.  Hardware identity fields (DevEUI,
// JoinEUI, AppKey, serial numbers, firmware) are excluded — those belong to
// later hardware-binding rounds.
// ---------------------------------------------------------------------------

// ---- Step 1: Client --------------------------------------------------------
export interface ClientInfo {
  display_name: string;
  legal_name: string;
  country: string;
  language: string;
  timezone: string;
  currency: string;
  email: string;
  phone: string;
  address: string;
}

// ---- Step 2: Exploitation / Farm ------------------------------------------
export interface FarmInfo {
  farm_name: string;
  address: string;
  country: string;
  surface_ha?: number;
  latitude?: number;
  longitude?: number;
}

// ---- Step 3: Primary User --------------------------------------------------
export type PrimaryUserRole = 'owner' | 'farm_manager' | 'technician' | 'viewer';

export interface PrimaryUserInfo {
  first_name: string;
  last_name: string;
  email: string;
  role: PrimaryUserRole;
}

// ---- Step 4: Commercial Scope ----------------------------------------------
export interface CommercialScope {
  command_packs: number;
  vision_packs: number;
  ia_packs: number;
  planned_gateways: number;
  fertigation: boolean;
  pump_control: boolean;
  pump_rated_power_kw?: number;
}

// ---- Step 5: Mapping -------------------------------------------------------
export type MapFileKind = 'kml' | 'geojson' | null;

export interface MappingInfo {
  file_kind: MapFileKind;
  file_name: string | null;
  file_size_bytes: number | null;
  manual_count: number;
}

// ---- Step 6: Infrastructure ------------------------------------------------
export type InfrastructureCategory =
  | 'water_source'
  | 'pump'
  | 'filter'
  | 'fertigation_unit'
  | 'collector'
  | 'valve'
  | 'flow_meter'
  | 'pressure_sensor'
  | 'gateway'
  | 'valve_node'
  | 'sensor_node'
  | 'relay_node'
  | 'adc_node'
  | 'ct_monitor';

export type InfrastructureLifecycle =
  | 'PLANNED'
  | 'AWAITING_HARDWARE'
  | 'UNASSIGNED';

export interface PlannedInfrastructureItem {
  id: string;
  category: InfrastructureCategory;
  label: string;
  lifecycle: InfrastructureLifecycle;
  quantity: number;
}

// ---- Step 7: TTN Readiness -------------------------------------------------
export type TtnStatus =
  | 'NOT_CONFIGURED'
  | 'TO_VERIFY'
  | 'AWAITING_HARDWARE'
  | 'DISABLED';

export interface TtnReadiness {
  application: TtnStatus;
  webhook: TtnStatus;
  gateway: TtnStatus;
  end_devices: TtnStatus;
  commands: TtnStatus;
  downlinks: TtnStatus;
  commissioning: TtnStatus;
}

// ---- Safety flags (immutable in R1) ----------------------------------------
export interface SafetyFlags {
  commands_enabled: false;
  downlink_enabled: false;
  commissioned: false;
}

// ---- Complete draft --------------------------------------------------------
export interface ClientOnboardingDraft {
  client: Partial<ClientInfo>;
  farms: FarmInfo[];
  primary_user: Partial<PrimaryUserInfo>;
  commercial_scope: Partial<CommercialScope>;
  mapping: MappingInfo;
  infrastructure: PlannedInfrastructureItem[];
  ttn_readiness: TtnReadiness;
  safety: SafetyFlags;
}

// ---- Default factories -----------------------------------------------------

export const EMPTY_CLIENT_INFO: ClientInfo = {
  display_name: '',
  legal_name: '',
  country: '',
  language: 'fr',
  timezone: 'Europe/Paris',
  currency: 'EUR',
  email: '',
  phone: '',
  address: '',
};

function cloneClientInfo(): ClientInfo {
  return {
    display_name: '',
    legal_name: '',
    country: '',
    language: 'fr',
    timezone: 'Europe/Paris',
    currency: 'EUR',
    email: '',
    phone: '',
    address: '',
  };
}

export const EMPTY_FARM: FarmInfo = {
  farm_name: '',
  address: '',
  country: '',
};

function cloneFarm(): FarmInfo {
  return { farm_name: '', address: '', country: '' };
}

export const EMPTY_PRIMARY_USER: PrimaryUserInfo = {
  first_name: '',
  last_name: '',
  email: '',
  role: 'farm_manager',
};

function clonePrimaryUser(): PrimaryUserInfo {
  return { first_name: '', last_name: '', email: '', role: 'farm_manager' };
}

function cloneCommercialScope(): Partial<CommercialScope> {
  return {
    command_packs: 0,
    vision_packs: 0,
    ia_packs: 0,
    planned_gateways: 0,
    fertigation: false,
    pump_control: false,
  };
}

function cloneMapping(): MappingInfo {
  return { file_kind: null, file_name: null, file_size_bytes: null, manual_count: 0 };
}

function cloneTtnReadiness(): TtnReadiness {
  return {
    application: 'NOT_CONFIGURED',
    webhook: 'TO_VERIFY',
    gateway: 'AWAITING_HARDWARE',
    end_devices: 'AWAITING_HARDWARE',
    commands: 'DISABLED',
    downlinks: 'DISABLED',
    commissioning: 'DISABLED',
  };
}

function cloneSafety(): SafetyFlags {
  return { commands_enabled: false, downlink_enabled: false, commissioned: false };
}

/**
 * Return a fully-isolated fresh ClientOnboardingDraft.  Every call produces
 * independent nested objects — no mutable reference is shared between calls.
 */
export function createEmptyClientOnboardingDraft(): ClientOnboardingDraft {
  return {
    client: cloneClientInfo(),
    farms: [cloneFarm()],
    primary_user: clonePrimaryUser(),
    commercial_scope: cloneCommercialScope(),
    mapping: cloneMapping(),
    infrastructure: [],
    ttn_readiness: cloneTtnReadiness(),
    safety: cloneSafety(),
  };
}

export const EMPTY_DRAFT: ClientOnboardingDraft = {
  client: { ...EMPTY_CLIENT_INFO },
  farms: [{ ...EMPTY_FARM }],
  primary_user: { ...EMPTY_PRIMARY_USER },
  commercial_scope: {
    command_packs: 0,
    vision_packs: 0,
    ia_packs: 0,
    planned_gateways: 0,
    fertigation: false,
    pump_control: false,
  },
  mapping: {
    file_kind: null,
    file_name: null,
    file_size_bytes: null,
    manual_count: 0,
  },
  infrastructure: [],
  ttn_readiness: {
    application: 'NOT_CONFIGURED',
    webhook: 'TO_VERIFY',
    gateway: 'AWAITING_HARDWARE',
    end_devices: 'AWAITING_HARDWARE',
    commands: 'DISABLED',
    downlinks: 'DISABLED',
    commissioning: 'DISABLED',
  },
  safety: {
    commands_enabled: false,
    downlink_enabled: false,
    commissioned: false,
  },
};

// ---------------------------------------------------------------------------
// R2I-03: DynamoDB-safe field length limits (mirrors backend constants)
// ---------------------------------------------------------------------------

// R2J-03: Unicode code-point-aware character counting
// Backend field max_length constraints count Unicode code points, not UTF-16
// code units.  This helper mirrors those semantics on the frontend.
//
// R2L: Normalize to NFKC + trim BEFORE counting so that character-count and
// UTF-8 budget validations operate on canonical text — matching the backend's
// Pydantic mode="before" validators.

/** Normalize onboarding text to canonical form: NFKC + trim.
 *
 * Mirrors the backend's ``normalize_text`` from
 * ``agrisafe.onboarding.normalization`` so that frontend validation
 * operates on the same canonical text the planner will use.
 */
export function normalizeOnboardingText(value: string): string {
  return value.normalize('NFKC').trim();
}

export function unicodeCodePointLength(value: string): number {
  return Array.from(normalizeOnboardingText(value)).length;
}

export const MAX_DISPLAY_NAME_LEN = 200;
export const MAX_LEGAL_NAME_LEN = 255;
export const MAX_PHONE_LEN = 64;
export const MAX_ADDRESS_LEN = 4096;
export const MAX_FARM_NAME_LEN = 200;
export const MAX_FARM_ADDRESS_LEN = 4096;
export const MAX_PRIMARY_FIRST_NAME_LEN = 100;
export const MAX_PRIMARY_LAST_NAME_LEN = 100;

// R2Q-01: Application-level client email total length contract.
// Backend and frontend share the same explicit constant — both sides
// reject overlong client emails before the POST boundary.
export const MAX_CLIENT_EMAIL_LEN = 254;

// R2M-03/R2M-05: Cognito ListUsers filter has a 256-character maximum.  The
// identity adapter constructs the filter as ``email = "{value}"`` which consumes
// 10 characters of fixed syntax, leaving 246 characters for the email.  Emails
// longer than this must be rejected at the frontend validation boundary before
// they ever reach the backend/Cognito.
export const MAX_PRIMARY_EMAIL_FILTER_SAFE_LEN = 246;
export const MAX_MAPPING_FILE_NAME_LEN = 255;
export const MAX_INFRA_ID_LEN = 128;
export const MAX_INFRA_LABEL_LEN = 255;

// R2J-05 / R2R-P2-01: Canonical two-letter country contract.
// Accepts lowercase input locally but normalizes on submission.
// Only assigned ISO 3166-1 alpha-2 codes are valid.
export const COUNTRY_CODE_RE = /^[A-Za-z]{2}$/;

// Assigned ISO 3166-1 alpha-2 country codes (249 entries).
// Mirrors the backend _VALID_ISO_COUNTRY_CODES frozenset.
// Excludes reserved, unassigned, and user-assigned codes.
export const VALID_ISO_COUNTRY_CODES: ReadonlySet<string> = new Set([
  "AD","AE","AF","AG","AI","AL","AM","AO","AQ","AR","AS","AT","AU","AW","AX","AZ",
  "BA","BB","BD","BE","BF","BG","BH","BI","BJ","BL","BM","BN","BO","BQ","BR","BS",
  "BT","BV","BW","BY","BZ","CA","CC","CD","CF","CG","CH","CI","CK","CL","CM","CN",
  "CO","CR","CU","CV","CW","CX","CY","CZ","DE","DJ","DK","DM","DO","DZ","EC","EE",
  "EG","EH","ER","ES","ET","FI","FJ","FK","FM","FO","FR","GA","GB","GD","GE","GF",
  "GG","GH","GI","GL","GM","GN","GP","GQ","GR","GS","GT","GU","GW","GY","HK","HM",
  "HN","HR","HT","HU","ID","IE","IL","IM","IN","IO","IQ","IR","IS","IT","JE","JM",
  "JO","JP","KE","KG","KH","KI","KM","KN","KP","KR","KW","KY","KZ","LA","LB","LC",
  "LI","LK","LR","LS","LT","LU","LV","LY","MA","MC","MD","ME","MF","MG","MH","MK",
  "ML","MM","MN","MO","MP","MQ","MR","MS","MT","MU","MV","MW","MX","MY","MZ","NA",
  "NC","NE","NF","NG","NI","NL","NO","NP","NR","NU","NZ","OM","PA","PE","PF","PG",
  "PH","PK","PL","PM","PN","PR","PS","PT","PW","PY","QA","RE","RO","RS","RU","RW",
  "SA","SB","SC","SD","SE","SG","SH","SI","SJ","SK","SL","SM","SN","SO","SR","SS",
  "ST","SV","SX","SY","SZ","TC","TD","TF","TG","TH","TJ","TK","TL","TM","TN","TO",
  "TR","TT","TV","TW","TZ","UA","UG","UM","US","UY","UZ","VA","VC","VE","VG","VI",
  "VN","VU","WF","WS","YE","YT","ZA","ZM","ZW",
]);

// R2J-01: Collection cardinality limits (mirrors backend MAX_FARMS_PER_DRAFT / MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT)
export const MAX_FARMS_PER_DRAFT = 100;
export const MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT = 500;

// R2O-02: R3-persistable integer counts (Number.MAX_SAFE_INTEGER = 2^53 - 1).
// Ensures exact integer semantics across JavaScript JSON/UI, deterministic
// frontend/backend parity, and comfortably fits inside DynamoDB numeric limits.
export const MAX_PERSISTED_COUNT = 9_007_199_254_740_991;

// ---------------------------------------------------------------------------
// R2P-01: DynamoDB-safe projected float envelope
// ---------------------------------------------------------------------------
// DynamoDB Number supports a maximum of 38 digits of precision with a
// non-zero positive range of approximately 1E-130 through 9.999...E+125.
// Values outside this range cannot be persisted by the canonical
// DomainRepository, which converts to Decimal(str(value)) before writing.
//
// These are TECHNICAL PERSISTENCE GUARDS, not business-domain limits.

export const DDB_SAFE_FLOAT_MIN_NONZERO = 1e-130;
export const DDB_SAFE_FLOAT_MAX = 1e125;

/**
 * Return true if *value* is within the DynamoDB-safe numeric envelope.
 *
 * @param value - The number to check.
 * @param allowZero - When true, exactly zero is accepted.
 */
export function isPersistenceSafeNumber(value: number, allowZero: boolean): boolean {
  if (!isFinite(value)) return false;
  if (value === 0) return allowZero;

  const magnitude = Math.abs(value);
  return magnitude >= DDB_SAFE_FLOAT_MIN_NONZERO && magnitude <= DDB_SAFE_FLOAT_MAX;
}

export const ORGANIZATION_USER_TEXT_BUDGET_BYTES = 32 * 1024; // 32 KiB
export const FARM_USER_TEXT_BUDGET_BYTES = 16 * 1024; // 16 KiB

// ---------------------------------------------------------------------------
// R1A-02: Centralised draft validation
// ---------------------------------------------------------------------------

export type ValidationErrors = Record<string, string>;

// ---------------------------------------------------------------------------
// R2P-02: Agri Safe onboarding email syntax contract
// ---------------------------------------------------------------------------
// A CONSERVATIVE SUBSET of EmailStr enforced identically on the backend and
// frontend.  The caller MUST already have applied NFKC normalization and
// trimming via normalizeOnboardingText().
//
// Rules:
//   - Exactly one @
//   - Local part: ASCII dot-atom — A-Z a-z 0-9 !#$%&'*+/=?^_`{|}~- plus dots
//     - non-empty, no leading/trailing dot, no consecutive dots
//   - Domain: dot-separated DNS labels
//     - each label: ASCII letters/digits/hyphen, non-empty, no leading/trailing hyphen
//     - at least one dot (no bare hostname)
//     - no consecutive dots, no underscore, no whitespace
//
// Intentionally NOT supported: quoted-string mailboxes, domain literals,
// comments, display-name syntax.

const AGRI_SAFE_EMAIL_ATOM = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+$/;
const AGRI_SAFE_EMAIL_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;
const AGRI_SAFE_EMAIL_LOCAL = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const AGRI_SAFE_EMAIL_DOMAIN = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export function isValidOnboardingEmail(value: string | null | undefined): boolean {
  if (!value) return false;
  const normalized = normalizeOnboardingText(value);
  if (!normalized) return false;

  const atIndex = normalized.indexOf('@');
  // Exactly one @, not at start or end
  if (atIndex < 1 || atIndex !== normalized.lastIndexOf('@') || atIndex === normalized.length - 1) {
    return false;
  }

  const localPart = normalized.slice(0, atIndex);
  const domainPart = normalized.slice(atIndex + 1);

  // Local part: ASCII dot-atom, no leading/trailing/consecutive dots
  if (!AGRI_SAFE_EMAIL_LOCAL.test(localPart)) return false;

  // R2R-P2-01: EmailStr-compatible local-part length (RFC 5321 §4.5.3.1).
  // The backend EmailStr enforces a 64-character local-part maximum.
  if (localPart.length > MAX_EMAIL_LOCAL_PART_LENGTH) return false;

  // Domain: dot-separated DNS labels with at least one dot
  if (!AGRI_SAFE_EMAIL_DOMAIN.test(domainPart)) return false;

  return true;
}

/** RFC 5321 §4.5.3.1 maximum local-part octet count (mirrors EmailStr). */
export const MAX_EMAIL_LOCAL_PART_LENGTH = 64;

/**
 * Validate the entire client onboarding draft.
 *
 * Returns raw error keys only (no translated messages).  The caller resolves
 * keys to human-readable text via its own t() scope.  This keeps translation
 * resolution inside components, satisfying the i18n source-usage audit.
 */
const ALLOWED_LANGUAGES = ['fr', 'en', 'es', 'ar', 'it'];
const CURRENCY_RE = /^[A-Z]{3}$/;

/** Resolve a timezone string to its canonical IANA identifier.
 *
 * Uses Intl.DateTimeFormat.resolvedOptions().timeZone to obtain the
 * exact, case-correct IANA name — the same authority that the backend's
 * zoneinfo.available_timezones() uses.
 *
 * Returns the canonical timezone string, or ``null`` if the input is
 * not a recognised IANA timezone.
 */
export function canonicalizeTimezone(tz: string): string | null {
  const trimmed = tz?.trim();
  if (!trimmed) return null;
  try {
    // Intl.DateTimeFormat throws RangeError for invalid timezone identifiers.
    // resolvedOptions().timeZone returns the canonical IANA form (e.g.
    // "europe/paris" → "Europe/Paris").
    const canonical = new (Intl.DateTimeFormat as any)([], { timeZone: trimmed })
      .resolvedOptions()
      .timeZone as string;
    return canonical || null;
  } catch {
    return null;
  }
}

/** Validate a timezone string against the browser's IANA database via
 * Intl.DateTimeFormat, using canonical form for exact parity with the
 * backend's zoneinfo.available_timezones(). */
function isValidTimezone(tz: string): boolean {
  return canonicalizeTimezone(tz) !== null;
}

/** Validate a country code against the assigned ISO 3166-1 alpha-2 set.
 *
 * R2R-P2-01: In addition to the two-letter shape check, the normalized
 * value must be a member of the assigned ISO country-code set.  Unassigned
 * codes such as "ZZ" are rejected.
 *
 * Applies NFKC + trim + uppercase before validation, matching the
 * backend's normalize_country pipeline.
 */
export function isValidCountryCode(value: string): boolean {
  const normalized = normalizeOnboardingText(value).toUpperCase();
  return COUNTRY_CODE_RE.test(normalized) && VALID_ISO_COUNTRY_CODES.has(normalized);
}

export function validateDraft(draft: ClientOnboardingDraft): string[] {
  const keys: string[] = [];

  // --- CLIENT ---
  // R2J-03: All string validations use Unicode code-point counting.
  const cpn = unicodeCodePointLength;
  if (!draft.client.display_name || cpn(draft.client.display_name) < 2)
    keys.push('client.display_name');
  else if (cpn(draft.client.display_name) > MAX_DISPLAY_NAME_LEN)
    keys.push('client.display_name_max');
  if (!draft.client.legal_name || cpn(draft.client.legal_name) < 2)
    keys.push('client.legal_name');
  else if (cpn(draft.client.legal_name) > MAX_LEGAL_NAME_LEN)
    keys.push('client.legal_name_max');
  // Country: exactly 2 ASCII letters + ISO 3166-1 membership (R2J-05 + R2R-P2-01)
  // R2M-01: Canonicalize via NFKC + trim before regex test.
  if (!draft.client.country || !isValidCountryCode(draft.client.country))
    keys.push('client.country_invalid');
  const clientEmail = draft.client.email;
  if (!clientEmail || !isValidOnboardingEmail(clientEmail))
    keys.push('client.email');
  else if (unicodeCodePointLength(clientEmail) > MAX_CLIENT_EMAIL_LEN)
    keys.push('client.email_max');
  // Phone: max length only (optional field)
  if (draft.client.phone && cpn(draft.client.phone) > MAX_PHONE_LEN)
    keys.push('client.phone_max');
  // Address: max length only (optional field)
  if (draft.client.address && cpn(draft.client.address) > MAX_ADDRESS_LEN)
    keys.push('client.address_max');
  // Language: allow-listed values (server parity)
  // R2M-01: Canonicalize via NFKC + trim before allow-list check.
  if (!draft.client.language || !ALLOWED_LANGUAGES.includes(normalizeOnboardingText(draft.client.language).toLowerCase()))
    keys.push('client.language');
  // Currency: ^[A-Z]{3}$ (server parity)
  if (!draft.client.currency || !CURRENCY_RE.test(normalizeOnboardingText(draft.client.currency).toUpperCase()))
    keys.push('client.currency');
  // Timezone: IANA validation (server parity)
  if (!draft.client.timezone || !isValidTimezone(normalizeOnboardingText(draft.client.timezone)))
    keys.push('client.timezone');

  // --- FARMS ---
  if (draft.farms.length === 0)
    keys.push('farms.empty');
  if (draft.farms.length > MAX_FARMS_PER_DRAFT)
    keys.push('farms.too_many');
  draft.farms.forEach((f, i) => {
    if (!f.farm_name || cpn(f.farm_name) < 2)
      keys.push(`farms.${i}.farm_name`);
    else if (cpn(f.farm_name) > MAX_FARM_NAME_LEN)
      keys.push(`farms.${i}.farm_name_max`);
    // Farm address: max length only (optional)
    if (f.address && cpn(f.address) > MAX_FARM_ADDRESS_LEN)
      keys.push(`farms.${i}.address_max`);
    // Farm country: optional, but if non-empty must be 2 ASCII letters (R2J-05)
    // R2M-01: Canonicalize via NFKC + trim before regex test.
    if (f.country && normalizeOnboardingText(f.country).length > 0 && !isValidCountryCode(f.country))
      keys.push(`farms.${i}.country_invalid`);
    if (f.surface_ha != null && (typeof f.surface_ha !== 'number' || !isFinite(f.surface_ha) || f.surface_ha < 0 || !isPersistenceSafeNumber(f.surface_ha, true)))
      keys.push(`farms.${i}.surface_ha`);
    // Coordinates: both present or both absent (server parity)
    const hasLat = f.latitude != null;
    const hasLon = f.longitude != null;
    if (hasLat !== hasLon) {
      if (!hasLat) keys.push(`farms.${i}.latitude`);
      if (!hasLon) keys.push(`farms.${i}.longitude`);
    }
    if (hasLat && (typeof f.latitude !== 'number' || !isFinite(f.latitude) || f.latitude < -90 || f.latitude > 90 || !isPersistenceSafeNumber(f.latitude, true)))
      keys.push(`farms.${i}.latitude`);
    if (hasLon && (typeof f.longitude !== 'number' || !isFinite(f.longitude) || f.longitude < -180 || f.longitude > 180 || !isPersistenceSafeNumber(f.longitude, true)))
      keys.push(`farms.${i}.longitude`);
  });

  // --- PRIMARY USER ---
  // R2J parity: backend min_length=1, frontend must match.
  if (!draft.primary_user.first_name || cpn(draft.primary_user.first_name) < 1)
    keys.push('primary_user.first_name');
  else if (cpn(draft.primary_user.first_name) > MAX_PRIMARY_FIRST_NAME_LEN)
    keys.push('primary_user.first_name_max');
  if (!draft.primary_user.last_name || cpn(draft.primary_user.last_name) < 1)
    keys.push('primary_user.last_name');
  else if (cpn(draft.primary_user.last_name) > MAX_PRIMARY_LAST_NAME_LEN)
    keys.push('primary_user.last_name_max');
  const primaryEmail = draft.primary_user.email;
  if (!primaryEmail || !isValidOnboardingEmail(primaryEmail))
    keys.push('primary_user.email');
  // R2M-05: Primary email must not exceed the Cognito ListUsers filter safe length.
  // Measure AFTER canonical normalization (NFKC + trim) to match backend semantics.
  else if (unicodeCodePointLength(primaryEmail) > MAX_PRIMARY_EMAIL_FILTER_SAFE_LEN)
    keys.push('primary_user.email_max');
  // R1E Sweep C: Runtime role validation
  const VALID_ROLES: PrimaryUserRole[] = ['owner', 'farm_manager', 'technician', 'viewer'];
  if (draft.primary_user.role && !VALID_ROLES.includes(draft.primary_user.role as PrimaryUserRole))
    keys.push('primary_user.role');

  // --- COMMERCIAL SCOPE ---
  // R2O-02: Number.isSafeInteger ensures frontend/backend parity with
  // MAX_PERSISTED_COUNT.  Unsafe JS integers (>2^53-1) are rejected
  // even if they appear to satisfy the min/max bounds.
  const cmdPacks = draft.commercial_scope.command_packs ?? 0;
  const visPacks = draft.commercial_scope.vision_packs ?? 0;
  const iaPacks = draft.commercial_scope.ia_packs ?? 0;
  const plannedGw = draft.commercial_scope.planned_gateways ?? 0;
  if (!Number.isSafeInteger(cmdPacks) || cmdPacks < 0 || cmdPacks > MAX_PERSISTED_COUNT)
    keys.push('commercial.command_packs');
  if (!Number.isSafeInteger(visPacks) || visPacks < 0 || visPacks > MAX_PERSISTED_COUNT)
    keys.push('commercial.vision_packs');
  if (!Number.isSafeInteger(iaPacks) || iaPacks < 0 || iaPacks > MAX_PERSISTED_COUNT)
    keys.push('commercial.ia_packs');
  if (!Number.isSafeInteger(plannedGw) || plannedGw < 0 || plannedGw > MAX_PERSISTED_COUNT)
    keys.push('commercial.planned_gateways');
  // R2D-01: Mirror backend pump_power_coherent contract
  // pump_control=false + non-null power → backend rejects (fail-closed)
  if (!draft.commercial_scope.pump_control && draft.commercial_scope.pump_rated_power_kw != null) {
    keys.push('commercial.pump_power');
  }
  // pump_control=true + power present → validate positive number within persistence envelope
  if (draft.commercial_scope.pump_control && draft.commercial_scope.pump_rated_power_kw != null) {
    const pw = draft.commercial_scope.pump_rated_power_kw;
    if (typeof pw !== 'number' || !isFinite(pw) || pw <= 0 || !isPersistenceSafeNumber(pw, false))
      keys.push('commercial.pump_power');
  }

  // --- MAPPING ---
  // R2C-03: Mirror backend mapping consistency contract
  if (!Number.isSafeInteger(draft.mapping.manual_count) || draft.mapping.manual_count < 0 || draft.mapping.manual_count > MAX_PERSISTED_COUNT)
    keys.push('mapping.manual_count');

  if (draft.mapping.file_kind === null) {
    // No file → file_name and file_size_bytes must both be null
    if (draft.mapping.file_name !== null) keys.push('mapping.file_name');
    if (draft.mapping.file_size_bytes !== null) keys.push('mapping.file_size_bytes');
  } else if (draft.mapping.file_kind === 'kml' || draft.mapping.file_kind === 'geojson') {
    // File present → file_name must be non-empty string
    if (!draft.mapping.file_name || cpn(draft.mapping.file_name) === 0)
      keys.push('mapping.file_name');
    else if (cpn(draft.mapping.file_name) > MAX_MAPPING_FILE_NAME_LEN)
      keys.push('mapping.file_name_max');
    // file_size_bytes must be a positive integer within bounds
    const sz = draft.mapping.file_size_bytes;
    if (sz === null || typeof sz !== 'number' || !Number.isInteger(sz) || sz < 0 || sz > 10_485_760)
      keys.push('mapping.file_size');
  }

  // --- INFRASTRUCTURE ---
  const VALID_CATEGORIES: InfrastructureCategory[] = [
    'water_source', 'pump', 'filter', 'fertigation_unit', 'collector',
    'valve', 'flow_meter', 'pressure_sensor', 'gateway',
    'valve_node', 'sensor_node', 'relay_node', 'adc_node', 'ct_monitor',
  ];
  const VALID_LIFECYCLES: InfrastructureLifecycle[] = ['PLANNED', 'AWAITING_HARDWARE', 'UNASSIGNED'];
  draft.infrastructure.forEach((item, idx) => {
    // Stable key for items whose id is missing/whitespace
    const idOk = item.id && item.id.trim().length > 0;
    const itemKey = idOk ? item.id : ('i' + idx);
    // R2C-02: Validate required trimmed fields (server parity)
    if (!item.id || cpn(item.id) === 0)
      keys.push(`infra.${itemKey}.id`);
    else if (cpn(item.id) > MAX_INFRA_ID_LEN)
      keys.push(`infra.${itemKey}.id_max`);
    if (!item.label || cpn(item.label) === 0)
      keys.push(`infra.${itemKey}.label`);
    else if (cpn(item.label) > MAX_INFRA_LABEL_LEN)
      keys.push(`infra.${itemKey}.label_max`);
    if (!Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > MAX_PERSISTED_COUNT)
      keys.push(`infra.${itemKey}.quantity`);
    // R1E Sweep C: Runtime enum validation
    if (!VALID_CATEGORIES.includes(item.category))
      keys.push(`infra.${itemKey}.category`);
    if (!VALID_LIFECYCLES.includes(item.lifecycle))
      keys.push(`infra.${itemKey}.lifecycle`);
  });

  if (draft.infrastructure.length > MAX_INFRASTRUCTURE_ITEMS_PER_DRAFT)
    keys.push('infrastructure.too_many');

  // --- AGGREGATE UTF-8 BUDGET (R2I-03) ---
  // Mirror the backend's conservative byte-budget validation.
  // Use TextEncoder when available (browser), fall back to manual
  // UTF-8 byte counting for jsdom test environments.
  const byteLength = (typeof TextEncoder !== 'undefined')
    ? (s: string) => new TextEncoder().encode(s).length
    : (s: string) => {
        // Manual UTF-8 byte counting for environments without TextEncoder
        let len = 0;
        for (let i = 0; i < s.length; i++) {
          const cp = s.codePointAt(i)!;
          if (cp <= 0x7F) len += 1;
          else if (cp <= 0x7FF) len += 2;
          else if (cp <= 0xFFFF) len += 3;
          else { len += 4; i++; } // surrogate pair
        }
        return len;
      };

  // R2L: Normalize all fields to NFKC + trim before measuring UTF-8 byte
  // budgets — matching the backend's Pydantic mode="before" validators.
  const orgFields = [
    normalizeOnboardingText(draft.client.display_name || ''),
    normalizeOnboardingText(draft.client.legal_name || ''),
    normalizeOnboardingText(draft.client.country || ''),
    normalizeOnboardingText(draft.client.email || ''),
    normalizeOnboardingText(draft.client.phone || ''),
    normalizeOnboardingText(draft.client.address || ''),
    normalizeOnboardingText(draft.client.language || ''),
    normalizeOnboardingText(draft.client.timezone || ''),
    normalizeOnboardingText(draft.client.currency || ''),
  ];
  const orgBytes = orgFields.reduce((sum, v) => sum + byteLength(v), 0);
  if (orgBytes > ORGANIZATION_USER_TEXT_BUDGET_BYTES)
    keys.push('client.aggregate_too_large');

  draft.farms.forEach((f, i) => {
    const farmFields = [
      normalizeOnboardingText(f.farm_name || ''),
      normalizeOnboardingText(f.address || ''),
      normalizeOnboardingText(f.country || ''),
    ];
    const farmBytes = farmFields.reduce((sum, v) => sum + byteLength(v), 0);
    if (farmBytes > FARM_USER_TEXT_BUDGET_BYTES)
      keys.push(`farms.${i}.aggregate_too_large`);
  });

  // --- SAFETY — fail-closed ---
  if (draft.safety.commands_enabled !== false)
    keys.push('safety.commands');
  if (draft.safety.downlink_enabled !== false)
    keys.push('safety.downlinks');
  if (draft.safety.commissioned !== false)
    keys.push('safety.commissioned');

  return keys;
}
